import { type RefObject, useEffect, useRef } from "react";

// How close to the bottom (px) still counts as "at the bottom".
const BOTTOM_THRESHOLD = 24;

/**
 * Keeps a scroll container pinned to the bottom as `dep` grows (new items
 * stream in), matching how the live product nudges the latest row into view.
 *
 * Behavior, per the product spec:
 *   - Auto-scroll is ON by default for each scene play.
 *   - If the user scrolls up, auto-scroll backs off and stays off.
 *   - If the user scrolls back to the bottom, auto-scroll resumes.
 *   - State resets on remount, so each replay starts pinned again (the user
 *     must scroll up again to turn it off).
 *
 * Programmatic scrolls are ignored while they settle so our own smooth-scroll
 * never disables sticking.
 */
export function useStickToBottom(
  ref: RefObject<HTMLElement | null>,
  dep: number,
  reduce: boolean,
) {
  const stick = useRef(true);
  const programmatic = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      const atBottom = distance <= BOTTOM_THRESHOLD;
      if (programmatic.current) {
        // Wait for our own scroll to reach the bottom before listening again.
        if (atBottom) programmatic.current = false;
        return;
      }
      stick.current = atBottom;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [ref]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pin on each reveal
  useEffect(() => {
    const el = ref.current;
    if (!el || !stick.current) return;
    programmatic.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: reduce ? "auto" : "smooth" });
  }, [dep, reduce]);
}
